import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { SectionCard, StatusBadge, formatNumber } from '../../../components/design-system/admin';
import type { JobHealth, OverviewResponse } from '../../../common/models/admin-overview.model';

/** One row per background pipeline: how the week went, and whether anything is stuck. */
export function HealthCard({ jobs, className }: { jobs: OverviewResponse['health']['jobs']; className?: string }) {
  const { t } = useTranslation();
  const rows: { key: string; label: string; j: JobHealth }[] = [
    { key: 'imports', label: t('ADMIN_OVERVIEW.HEALTH.IMPORTS'), j: jobs.imports },
    { key: 'seamless', label: t('ADMIN_OVERVIEW.HEALTH.SEAMLESS'), j: jobs.seamless },
    { key: 'models3d', label: t('ADMIN_OVERVIEW.HEALTH.MODELS3D'), j: jobs.models3d },
    { key: 'studio', label: t('ADMIN_OVERVIEW.HEALTH.STUDIO'), j: jobs.studio },
  ];

  return (
    <SectionCard
      title={t('ADMIN_OVERVIEW.HEALTH.TITLE')}
      description={t('ADMIN_OVERVIEW.HEALTH.DESCRIPTION')}
      action={
        <Button variant="ghost" size="sm" asChild>
          <Link to="/queue-monitor">{t('ADMIN_OVERVIEW.HEALTH.OPEN_QUEUES')}</Link>
        </Button>
      }
      className={className}
      testId="overview-health"
    >
      <ul className="flex flex-col gap-3.5">
        {rows.map(({ key, label, j }) => {
          const finished = j.completed + j.failed;
          const rate = finished ? Math.round((j.completed / finished) * 100) : null;
          const tone = j.stuck ? 'danger' : j.failed ? 'warning' : finished ? 'success' : 'neutral';
          return (
            <li key={key} className="flex flex-col gap-1.5" data-testid={`health-${key}`}>
              <div className="flex items-center justify-between gap-2">
                <StatusBadge tone={tone}>{label}</StatusBadge>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {rate === null ? t('ADMIN_OVERVIEW.HEALTH.IDLE') : t('ADMIN_OVERVIEW.HEALTH.SUCCESS_RATE', { rate })}
                </span>
              </div>
              <div className="flex h-1.5 overflow-hidden rounded-full bg-muted">
                {finished > 0 && (
                  <>
                    <div className="h-full bg-foreground/70" style={{ width: `${(j.completed / finished) * 100}%` }} />
                    <div className="h-full bg-destructive" style={{ width: `${(j.failed / finished) * 100}%` }} />
                  </>
                )}
              </div>
              <p className="flex flex-wrap gap-x-3 text-xs text-muted-foreground tabular-nums">
                <span>{t('ADMIN_OVERVIEW.HEALTH.COMPLETED', { count: j.completed })}</span>
                {!!j.failed && <span className="text-destructive">{t('ADMIN_OVERVIEW.HEALTH.FAILED', { count: j.failed })}</span>}
                {!!j.stuck && <span className="text-destructive">{t('ADMIN_OVERVIEW.HEALTH.STUCK', { count: j.stuck })}</span>}
              </p>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
